import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import React, { useEffect, useRef, useState, useStore } from "react";
import Constants from "./Constants";
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import axios from "axios";
import msgpack from 'msgpack-lite';
import { Button } from "@mui/base";

const VARIABLES = require('./variables.json');

function ProgressScreen(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const [progressPercent, setPercent] = useState(0);
    const [status, setProgStatus] = useState("Processing");
    const [redirect, setRedirectStatus] = useState(null);
    const [workflowId, setworkflowId] = useState(null);
    const timerRef = useRef(null);
    // const dispatch = useDispatch()
    const criteria = props.newCriteria || location.state;


    useEffect(() => {
        startWorkflow();
        window.scrollTo(0, 0);
        return () => {
            clearTimeout(timerRef.current);
            window.history.pushState('', '', reduceUrl()) // shorten URL
        }
    }, []);
    function setProgressPercent(p) {
        setPercent(p);
    }
    function setProgressStatus(s) {
        setProgStatus(s);
    }
    function setState(re) {
        setRedirectStatus(re);
    }

    /**
     * Initiates the workflow with a request to the backend and starts the
     * polling process.
     */
    const startWorkflow = () => {
        axios
            .post('api/start_workflow', criteria)
            .then(resp => {
                // Intercept Error
                if (resp.data.error) {
                    const { message } = resp.data;
                    const redirect = navigate(`/error/${message}`);
                    setState({ redirect });
                    return;
                }

                // Set Loading Status
                const {
                    workflowId,
                    status,
                    progressPercent
                } = resp.data;


                setworkflowId(workflowId);
                setProgressStatus(status);
                setProgressPercent(progressPercent);
                props.sharedStore.dispatch({
                    type: 'SET_WORKFLOWID',
                    value: workflowId  // for lasp download
                });
                tick(workflowId);
            })
            .catch(error => {
                const message = "An unknown error occurred generating the plots.";
                const redirect = navigate(`/error/${message}`);
                setState({ redirect });
            });
    }


    /**
     * Poll the backend for workflow updates.
     *
     * Breaks when the result is received.
     */
    function tick(workflowId) {
        axios
            .get(`api/poll_workflow?workflow_id=${workflowId}&tick=1`)
            .then(resp => {
                const {
                    status,
                    progressPercent,
                    done
                } = resp.data;

                if (done) {
                    getResults(workflowId);
                    dist();

                    return;
                }
                setworkflowId(workflowId);
                setProgStatus(status);
                setPercent(progressPercent)

                props.sharedStore.dispatch({
                    type: 'SET_WORKFLOWID',
                    value: workflowId  // for lasp download
                });


                timerRef.current = setTimeout(() =>
                    tick(workflowId), Constants.PROGRESS_POLL_INTERVAL);
            })
            .catch(error => {
                const message = "An unknown error occurred when checking on the workflow.";
                const redirect = navigate(`/error/${message}`);
            });
    }


    /**
       * Get the results from the backend and redirect to the result screen when
       * complete.
       *
       * Pre-Condition: The `poll_workflow` endpoint returned done=true
       */
    async function getResults(workflowId) {
        try {
            // Set progress tracking
            const progressPercentAtStart = progressPercent;
            const remaining = 100 - progressPercentAtStart;

            setProgStatus("Transferring plot data to your computer");
            
            const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks
            let chunkIndex = 0;
            let totalSize = 0;
            let receivedSize = 0;
            let buffer = new Uint8Array(0);
            
            // Fetch data in chunks
            while (true) {
                const resp = await axios.get(
                    `api/get_workflow_results_chunked?workflow_id=${workflowId}&chunk=${chunkIndex}&chunk_size=${CHUNK_SIZE}`,
                    { responseType: 'arraybuffer' }
                );
                
                // Get metadata from response headers
                const total_size = parseInt(resp.headers['x-total-size']);
                const chunk_start = parseInt(resp.headers['x-chunk-start']);
                const chunk_end = parseInt(resp.headers['x-chunk-end']);
                const is_final = resp.headers['x-is-final'] === 'true';
                
                if (chunkIndex === 0) {
                    totalSize = total_size;
                }
                
                // Get binary data directly (no base64 decode needed!)
                const bytes = new Uint8Array(resp.data);
                
                // Append to buffer
                const newBuffer = new Uint8Array(buffer.length + bytes.length);
                newBuffer.set(buffer);
                newBuffer.set(bytes, buffer.length);
                buffer = newBuffer;
                
                // Update progress bar
                receivedSize = chunk_end;
                const downloadProgress = Math.round((receivedSize / totalSize) * 100);
                const progressPercent = Math.round(
                    progressPercentAtStart + (downloadProgress / 100) * remaining
                );
                setPercent(progressPercent);
                
                if (is_final) break;
                chunkIndex++;
            }
            
            // Decode msgpack from complete buffer
            const results = msgpack.decode(buffer);
            
            // Clear buffer immediately to free memory
            buffer = null;
            
            if (!results) {
                const faux = tooMuchData('file transfer');
                props.sharedStore.dispatch({
                    type: 'SET_RESULTS',
                    value: faux
                });
            } else {
                props.sharedStore.dispatch({
                    type: 'SET_RESULTS',
                    value: results
                });
            }

            setTimeout(() => {
                var prep = JSON.stringify(criteria)
                props.setLoadingData(false);
                props.setPlotlyInnerElement('');
            }, 100);
            
        } catch (error) {
            console.error('Error in getResults:', error);
            const faux = tooMuchData('client processing');
            props.sharedStore.dispatch({
                type: 'SET_RESULTS',
                value: faux
            });

            setTimeout(() => {
                var prep = JSON.stringify(criteria)
                props.setLoadingData(false);
                props.setPlotlyInnerElement('');
            }, 100);
        }
    }

    // I was too lazy to extract this from ResultsScreen.js
    function tooMuchData(cause) {
        return {
            "layout": {
                "xaxis": {
                    "visible": false
                },
                "yaxis": {
                    "visible": false
                },
                "annotations": [
                    {
                        "text": "There is too much data to process based on the selected criteria (" + cause + ")",
                        "xref": "paper",
                        "yref": "paper",
                        "showarrow": false,
                        "font": {
                            "size": 28
                        }
                    }
                ]
            }
        }
    }

    function dist() {
        //This is hardcoded to display the example file

        // What happens is:
        // this bogus workflow id talks to  views.py get_workflow_results_dist()
        // the nominal dist plots are retrieved using get_workflow_results_dist4()
        // var", 'IONS_MMS3_perp1_perp2__2015-12-28_05:58:46.000000_500.json
        axios
            .get(`api/get_workflow_example_dist?workflow_id=fpi_web_vis-2022-04-14T18:29:08.939557_c60d9314`)
            .then(resp => {
                const { dist } = resp.data;

                if (!dist) {
                    const faux = this.tooMuchData('file transfer'); // file transfer failed (usually Chrome)
                    props.sharedStore.dispatch({
                        type: 'SET_DIST',
                        value: faux
                    });
                }
                else {
                    props.sharedStore.dispatch({
                        type: 'SET_DIST',
                        value: dist
                    });
                }

            })
            .catch(error => {
                const message = "An unknown error occurred when checking on the workflow.";
            });
        return;
    }


    function reduceUrl() {
        var prep = JSON.stringify(criteria)
        var jsc = JSON.parse(prep);
        // for each list, combine all the 2char ids into one string
        if (jsc.arrayorder && VARIABLES.categories) {
            VARIABLES.categories.forEach(category => {
                if (jsc.arrayorder[category.title] && Array.isArray(jsc.arrayorder[category.title])) {
                    var reduce = jsc.arrayorder[category.title];
                    var inside = ""
                    for (var i = 0; i < reduce.length; ++i) {
                        inside = inside + reduce[i]
                    }
                    jsc.arrayorder[category.title] = inside;
                } else {
                    // Initialize empty array if category doesn't exist
                    jsc.arrayorder[category.title] = "";
                }
            });
        } else {
            // Initialize arrayorder if it doesn't exist
            jsc.arrayorder = {};
            if (VARIABLES.categories) {
                VARIABLES.categories.forEach(category => {
                    jsc.arrayorder[category.title] = "";
                });
            }
        }
        //Create the URL
        const shortenedUrl = "/#/results/" + JSON.stringify(jsc);
        return shortenedUrl;
    }

    return (
        <div className="item plot" id="contains graph div">
            <div className='plotly-placement progress-bar-container' id="plotly-results">
                <div className="progress-status-text">{status}</div>
                <LinearProgress
                    variant="determinate"
                    value={progressPercent} />
            </div>
        </div>
    );
}

export default ProgressScreen;
